import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ClipboardCheck } from 'lucide-react';
import { reachConfigAPI, reachAssessmentsAPI, ReachEngagementConfig, ReachAssessment, AssessmentType } from '../../services/reachService';
import { ConfigGate } from '../../components/reach/common/ConfigGate';
import { AssessmentSelector } from '../../components/reach/assessments/AssessmentSelector';
import { AssessmentForm } from '../../components/reach/assessments/AssessmentForm';
import { toast } from 'sonner';

const TYPE_LABELS: Record<AssessmentType, string> = {
  champion: 'Champion',
  executive: 'Executive',
  'practice-leader': 'Practice Leader',
  user: 'End User',
  general: 'General',
};

const TYPE_COLORS: Record<AssessmentType, string> = {
  champion: 'bg-violet-50 text-violet-700 border-violet-200',
  executive: 'bg-blue-50 text-blue-700 border-blue-200',
  'practice-leader': 'bg-amber-50 text-amber-700 border-amber-200',
  user: 'bg-green-50 text-green-700 border-green-200',
  general: 'bg-slate-100 text-slate-600 border-slate-200',
};

const AssessmentsPage: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const [config, setConfig] = useState<ReachEngagementConfig | null | undefined>(undefined);
  const [selectedType, setSelectedType] = useState<AssessmentType | null>(null);
  const [assessments, setAssessments] = useState<ReachAssessment[]>([]);
  const [submitted, setSubmitted] = useState(false);
  const [lastType, setLastType] = useState<AssessmentType | null>(null);

  useEffect(() => {
    Promise.all([
      reachConfigAPI.get(projectId!).then(r => setConfig(r.data.config)).catch(() => setConfig(null)),
      reachAssessmentsAPI.list(projectId!).then(r => setAssessments(r.data.assessments)).catch(() => {})
    ]);
  }, [projectId]);

  const handleSubmit = async (data: any) => {
    try {
      const res = await reachAssessmentsAPI.submit(projectId!, data);
      setAssessments(prev => [...prev, res.data.assessment]);
      setLastType(selectedType);
      setSubmitted(true);
      toast.success('Assessment submitted successfully');
    } catch {
      toast.error('Failed to submit assessment');
      throw new Error('submit failed');
    }
  };

  const typeCounts = assessments.reduce<Partial<Record<AssessmentType, number>>>((acc, a) => {
    acc[a.type] = (acc[a.type] ?? 0) + 1;
    return acc;
  }, {});

  const recentAssessments = [...assessments]
    .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
    .slice(0, 5);

  if (config === undefined) return (
    <div className="flex items-center justify-center h-64 text-slate-400 font-display">Loading…</div>
  );

  return (
    <div className="flex-1 overflow-y-auto p-8 font-display">
      <div className="w-full max-w-[1400px] mx-auto flex flex-col gap-6">
        <header className="flex items-center justify-between border-b border-slate-200 pb-6">
          <div className="flex items-center gap-4">
            <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary/10 text-primary">
              <ClipboardCheck size={22} />
            </div>
            <div>
              <h1 className="text-2xl font-bold tracking-tight text-navy">Readiness Assessments</h1>
              <p className="text-slate-500 text-sm font-medium">{assessments.length} assessment{assessments.length !== 1 ? 's' : ''} submitted</p>
            </div>
          </div>
        </header>

        <ConfigGate hasConfig={!!config}>
          {submitted ? (
            <div className="flex flex-col items-center gap-4 py-16">
              <div className="w-16 h-16 rounded-full bg-green-50 flex items-center justify-center">
                <ClipboardCheck size={28} className="text-green-500" />
              </div>
              <p className="text-navy font-semibold">Assessment submitted</p>
              <p className="text-slate-500 text-sm">The scores have been recorded and will appear in the Results Dashboard.</p>
              <div className="flex gap-3">
                <button
                  onClick={() => { setSubmitted(false); setSelectedType(lastType); }}
                  className="rounded-xl h-10 px-5 bg-primary hover:bg-primary-dark text-white text-sm font-bold transition-colors"
                >
                  Submit another {lastType ? TYPE_LABELS[lastType] : ''}
                </button>
                <button onClick={() => navigate(`/projects/${projectId}/reach/results`)}
                  className="rounded-xl h-10 px-5 border border-slate-200 text-slate-600 text-sm font-bold hover:bg-slate-50 transition-colors">
                  View results
                </button>
              </div>
              {lastType && (
                <button
                  onClick={() => { setSubmitted(false); setSelectedType(null); }}
                  className="text-xs text-slate-400 hover:text-slate-600 underline underline-offset-2 transition-colors"
                >
                  Switch type
                </button>
              )}
            </div>
          ) : selectedType ? (
            <AssessmentForm
              type={selectedType}
              projectId={projectId!}
              onSubmit={handleSubmit}
              onBack={() => setSelectedType(null)}
            />
          ) : (
            <div className="grid grid-cols-[1fr_360px] gap-8 items-start">
              <AssessmentSelector onSelect={setSelectedType} counts={typeCounts} />

              {/* Recent submissions */}
              <div className="flex flex-col gap-3">
                <p className="text-[11px] font-bold text-primary tracking-widest uppercase">Recent submissions</p>
                {recentAssessments.length === 0 ? (
                  <div className="border border-[#e2e8f0] rounded-xl bg-white p-6 text-center">
                    <p className="text-sm text-slate-400">No assessments yet</p>
                  </div>
                ) : (
                  <div className="border border-[#e2e8f0] rounded-xl bg-white overflow-hidden">
                    {recentAssessments.map((a, i) => (
                      <div
                        key={a.id}
                        className={`flex items-center justify-between px-4 py-3 ${i < recentAssessments.length - 1 ? 'border-b border-[#e2e8f0]' : ''}`}
                      >
                        <div>
                          <p className="text-sm font-semibold text-navy">{a.respondentName || 'Anonymous'}</p>
                          <p className="text-xs text-slate-400 mt-0.5">{new Date(a.timestamp).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</p>
                        </div>
                        <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full border ${TYPE_COLORS[a.type]}`}>
                          {TYPE_LABELS[a.type]}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
                {assessments.length > 5 && (
                  <button
                    onClick={() => navigate(`/projects/${projectId}/reach/results`)}
                    className="text-xs text-primary hover:text-primary-dark font-semibold text-right transition-colors"
                  >
                    View all {assessments.length} in Results →
                  </button>
                )}
              </div>
            </div>
          )}
        </ConfigGate>
      </div>
    </div>
  );
};

export default AssessmentsPage;
